import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { ApiError, type FieldError } from '@/shared/http';

import { createMutationFeedback, type MutationFeedbackOptions } from './feedback';
import { useNotifier } from './notifier';

export interface ApiMutationOptions<TData, TVariables> extends MutationFeedbackOptions<TData, TVariables> {
  mutationFn: (variables: TVariables) => Promise<TData>;
  mutationKey?: readonly unknown[];
}

export type ApiMutationResult<TData, TVariables> = UseMutationResult<TData, ApiError, TVariables> & {
  /** Erros por campo do último `INVALID_FORM` (vazio sem erro); o formulário casa por nome. */
  fieldErrors: FieldError[];
};

/**
 * Mutação com o feedback padrão do legado: toast de sucesso, toast de erro mapeado
 * (`mapErrorResponse`) e invalidação por chave. Substitui o bloco `startRequest`/`setSubmitError`/
 * `showSuccessToast` repetido nos 263 `useMutation` de `contexts/` (P05 + P21).
 */
export function useApiMutation<TData = unknown, TVariables = void>(
  options: ApiMutationOptions<TData, TVariables>,
): ApiMutationResult<TData, TVariables> {
  const queryClient = useQueryClient();
  const notifier = useNotifier();
  const feedback = createMutationFeedback(notifier, queryClient, options);
  const mutation = useMutation<TData, ApiError, TVariables>({
    ...(options.mutationKey ? { mutationKey: options.mutationKey } : {}),
    mutationFn: async (variables) => {
      try {
        return await options.mutationFn(variables);
      } catch (error) {
        throw ApiError.from(error);
      }
    },
    onSuccess: (data, variables) => feedback.onSuccess(data, variables),
    onError: (error, variables) => feedback.onError(error, variables),
  });
  return { ...mutation, fieldErrors: mutation.error?.fieldErrors ?? [] };
}
